<template>
  <div class="video-post-container">
    <el-card class="box-card shadow-box steps-wrapper">
      <el-steps :active="currentStep" finish-status="success" align-center class="custom-steps">
        <el-step title="上传视频" icon="el-icon-upload" />
        <el-step title="设置封面" icon="el-icon-picture" />
        <el-step title="完善稿件信息" icon="el-icon-tickets" />
      </el-steps>

      <div class="step-content-router">
        <step-upload-video
          v-show="currentStep === 0"
          v-model="form"
          :is-edit="isEdit"
          @next="currentStep++"
          @video-success="handleVideoSuccess"
        />

        <step-upload-cover
          v-show="currentStep === 1"
          v-model="form"
          :video-file="videoRawFile"
          :cover-url.sync="coverUrl"
          :is-edit="isEdit"
          @next="currentStep++"
          @prev="currentStep--"
        />

        <step-form-info
          v-show="currentStep === 2"
          v-model="form"
          :is-edit="isEdit"
          :submitting="submitting"
          @prev="currentStep--"
          @submit="handleFinalSubmit"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import StepUploadVideo from '@/components/StepUploadVideo.vue'
import StepUploadCover from '@/components/StepUploadCover.vue'
import StepFormInfo from '@/components/StepFormInfo.vue'

export default {
  name: 'VideoPostPublish',
  components: { StepUploadVideo, StepUploadCover, StepFormInfo },
  props: {
    videoInfo: { type: Object, default: null }
  },
  data() {
    return {
      currentStep: 0,
      coverUrl: null,
      videoRawFile: null, // 核心：用于暂存第一步返回的本地 File 对象
      submitting: false,
      form: {
        videoId: null,
        coverFileId: null,
        title: '',
        description: '',
        categoryPid: null,
        categoryId: null,
        tags: [],
        scope: '2'
      }
    }
  },
  computed: {
    isEdit() { return this.videoInfo !== null }
  },
  watch: {
    videoInfo: {
      handler(val) { if (val) this.initEditData(val) },
      immediate: true
    }
  },
  methods: {
    initEditData(info) {
      this.form = {
        videoId: info.videoId,
        coverFileId: info.coverFileId || null,
        title: info.title || '',
        description: info.description || '',
        categoryPid: info.categoryPid || null,
        categoryId: info.categoryId || null,
        tags: Array.isArray(info.tags) ? info.tags : (info.tags ? info.tags.split(',') : []),
        scope: String(info.scope || '2')
      }
      this.coverUrl = info.coverUrl || null
      this.videoRawFile = null
      this.currentStep = 0
    },
    // 第一步视频上传成功，拿到本地原始文件
    handleVideoSuccess(rawFile) {
      this.videoRawFile = rawFile
    },
    handleFinalSubmit() {
      this.submitting = true
      this.$emit('video-publish', {
        data: this.form,
        mode: this.isEdit ? 'edit' : 'publish'
      })
      setTimeout(() => { this.submitting = false }, 1000)
    }
  }
}
</script>

<style scoped lang="scss">
.video-post-container {
  padding: 32px 20px;
  background-color: #f6f8fa;
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  justify-content: center;

  .steps-wrapper {
    width: 100%;
    max-width: 880px; // 限制表单总宽度，防止宽屏下横向拉伸过长导致视觉松散
    border: 1px solid #eaeefb;
    border-radius: 12px;
    box-shadow: 0 6px 16px -4px rgba(0, 0, 0, 0.04);
    padding: 12px;
  }

  .custom-steps {
    margin-bottom: 40px;
    ::v-deep .el-step__title {
      font-size: 14px;
      font-weight: 500;
    }
  }

  // 步骤内容切换区域
  .step-content-router {
    min-height: 320px;
  }

  // 淡入动效
  .fade-in {
    animation: fadeIn 0.35s ease-in-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  // 统一的下方操作栏
  .step-actions {
    margin-top: 36px;
    padding-top: 20px;
    border-top: 1px solid #f0f2f5;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.center {
      justify-content: center;
    }
  }
}

/* 封面上传样式优化 */
.cover-upload-wrapper {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 10px 0;

  .avatar-uploader {
    border: 2px dashed #e0e0e0;
    border-radius: 10px;
    cursor: pointer;
    overflow: hidden;
    width: 260px;
    height: 155px;
    background: #fafafa;
    transition: all 0.25s ease-in-out;
    flex-shrink: 0;

    &:hover {
      border-color: #409EFF;
      background: #f5f9ff;
    }
  }

  .uploader-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #909399;

    .placeholder-icon { font-size: 26px; margin-bottom: 8px; }
    .placeholder-text { font-size: 13px; }
  }

  .cover-preview {
    position: relative;
    width: 100%;
    height: 100%;
    .avatar { width: 100%; height: 100%; object-fit: cover; }
    .cover-mask {
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(29, 33, 41, 0.6); color: #fff;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
      gap: 6px; font-size: 13px; opacity: 0; transition: opacity 0.2s ease;
    }
    &:hover .cover-mask { opacity: 1; }
  }

  .cover-info-text {
    flex: 1;

    .tip-title {
      font-size: 14px;
      font-weight: 500;
      color: #606266;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 4px;
      i { color: #409EFF; }
    }
    .tip-item { font-size: 13px; color: #8c929e; margin: 6px 0; line-height: 1.6; }
  }
}

/* 表单精细化调整 */
.custom-form {
  ::v-deep .el-form-item__label {
    padding-bottom: 6px;
    font-weight: 500;
    color: #303133;
  }

  .custom-radio-group {
    width: 100%;
    display: flex;
    ::v-deep .el-radio-button {
      flex: 1;
      .el-radio-button__inner { width: 100%; text-align: center; }
    }
  }

  .tags-select {
    ::v-deep .el-select__tags { max-height: 80px; overflow-y: auto; }
  }
}
</style>
